import React, { useState } from "react";
import {
  Drawer,
  DatePicker,
  Space,
  message,
  Popconfirm,
  Typography,
  Divider,
} from "antd";
import { useDispatch, useSelector } from "react-redux";
import dayjsTZ from "../../../../utils/dayjs";
import { API } from "../../../../helpers/API/amplifyApi";
import { htmlParser } from "../../../../utils";
import { useAuth } from "../../../../authentication/authHelpers";
import { employeeRequests as employeeRequestsReducer } from "../../../../store/actions";
import MondayButton from "../../../commonComponents/MondayButton/MondayButton";
import { v4 as uuidv4 } from "uuid";
import { STATUS_COLORS } from "../utils/statusColors";

const { RangePicker } = DatePicker;
const { Text, Paragraph } = Typography;

const enumerateDaysBetweenDates = (startDate, endDate) => {
  const dates = [];
  let curr = dayjsTZ(startDate).startOf("day");
  const last = dayjsTZ(endDate).startOf("day");
  while (curr.isBefore(last) || curr.isSame(last, "day")) {
    dates.push(curr.valueOf());
    curr = curr.add(1, "day");
  }
  return dates;
};

const PlanerDrawer = ({ open, onClose, content, getIcon, canManage = false }) => {
  const { user } = useAuth();
  const dispatch = useDispatch();
  const { employeeRequests } = useSelector((state) => state.employeeRequests);
  const { isDarkMode } = useSelector((state) => state.usersList);
  const [range, setRange] = useState(null);
  const [busy, setBusy] = useState(false);

  if (!content) return null;

  const updateRedux = (patch, id, remove) => {
    const next = [...(employeeRequests || [])];
    const idx = next.findIndex((el) => el?.requestId === id);
    if (idx > -1) {
      if (remove) next.splice(idx, 1);
      else next[idx] = { ...next[idx], ...patch };
    }
    dispatch(employeeRequestsReducer(next));
  };

  const appendLog = (field, oldValue, newValue, activity = "Ndryshuar") => ({
    id: uuidv4(),
    activity,
    author: `${user?.given_name || ""} ${user?.family_name || ""}`.trim(),
    userSub: user?.userSub,
    changeDate: dayjsTZ().valueOf(),
    oldValue,
    newValue,
    field,
  });

  const persist = async (body) => {
    setBusy(true);
    try {
      await API.put("employeeRequests", `/employeeRequests/${content.requestId}`, {
        body,
      });
      updateRedux(body, content.requestId);
      message.success("Kërkesa u përditësua");
      onClose?.();
    } catch (e) {
      message.error("Përditësimi dështoi");
    } finally {
      setBusy(false);
    }
  };

  const setStatus = async (requestStatus) => {
    const keylog = appendLog(
      "Statusi i lejes",
      content.requestStatus,
      requestStatus
    );
    await persist({
      requestStatus,
      requestLogs: [...(content.requestLogs || []), keylog],
    });
  };

  const reschedule = async () => {
    if (!range?.[0] || !range?.[1]) {
      message.warning("Zgjidh datat e reja");
      return;
    }
    const requestPeriod = enumerateDaysBetweenDates(range[0], range[1]);
    const keylog = appendLog(
      "Periudha e lejes",
      `${dayjsTZ(content.requestPeriod[0]).format("DD/MM/YYYY")} – ${dayjsTZ(
        content.requestPeriod.at(-1)
      ).format("DD/MM/YYYY")}`,
      `${range[0].format("DD/MM/YYYY")} – ${range[1].format("DD/MM/YYYY")}`,
      "Riorganizuar"
    );
    await persist({
      requestPeriod,
      requestStatus: "Pending",
      requestLogs: [...(content.requestLogs || []), keylog],
    });
  };

  const remove = async () => {
    setBusy(true);
    try {
      await API.del("employeeRequests", `/employeeRequests/${content.requestId}`);
      updateRedux({}, content.requestId, true);
      message.success("Kërkesa u fshi");
      onClose?.();
    } catch (e) {
      message.error("Fshirja dështoi");
    } finally {
      setBusy(false);
    }
  };

  const statusColor = STATUS_COLORS[content.requestStatus] || "#888";

  return (
    <Drawer
      open={open}
      onClose={onClose}
      width={420}
      title={`Leje — ${content.given_name || ""} ${content.family_name || ""}`}
      className={`planer-drawer ${isDarkMode ? "is-dark" : "is-light"}`}
      destroyOnClose
    >
      <Space direction="vertical" size={16} style={{ width: "100%" }}>
        <div>
          <Text type="secondary">Statusi</Text>
          <div>
            <span className="planer-status-pill" style={{ background: statusColor }}>
              {content.requestStatus}
            </span>
          </div>
        </div>
        <div>
          <Text type="secondary">Lloji</Text>
          <Paragraph style={{ marginBottom: 0 }}>
            {getIcon ? getIcon(content.requestType) : null} {content.requestType}
          </Paragraph>
        </div>
        <div>
          <Text type="secondary">Periudha</Text>
          <Paragraph style={{ marginBottom: 0 }}>
            {dayjsTZ(content.requestPeriod?.[0]).format("DD/MM/YYYY")} –{" "}
            {dayjsTZ(content.requestPeriod?.at?.(-1)).format("DD/MM/YYYY")}
          </Paragraph>
        </div>
        {content.requestDescription ? (
          <div>
            <Text type="secondary">Përshkrimi</Text>
            <div className="planer-desc">{htmlParser(content.requestDescription)}</div>
          </div>
        ) : null}

        {canManage ? (
          <>
            <Divider style={{ margin: "8px 0" }} />

            <Space wrap>
              <MondayButton
                className="mondayButtonGreen"
                disabled={busy || content.requestStatus === "Aprovuar"}
                onClick={() => setStatus("Aprovuar")}
              >
                Aprovo
              </MondayButton>
              <MondayButton
                className="mondayButtonRed"
                disabled={busy || content.requestStatus === "Refuzuar"}
                onClick={() => setStatus("Refuzuar")}
              >
                Refuzo
              </MondayButton>
              <MondayButton
                className="mondayButtonBlue"
                disabled={busy || content.requestStatus === "Shtyre"}
                onClick={() => setStatus("Shtyre")}
              >
                Shty
              </MondayButton>
              <MondayButton
                className="mondayButtonYellow"
                disabled={busy || content.requestStatus === "Pending"}
                onClick={() => setStatus("Pending")}
              >
                Në pritje
              </MondayButton>
            </Space>

            <div>
              <Text type="secondary">Riorganizo datat</Text>
              <RangePicker
                style={{ width: "100%", marginTop: 8 }}
                format="DD/MM/YYYY"
                onChange={(vals) => setRange(vals)}
              />
              <MondayButton
                style={{ marginTop: 12 }}
                className="mondayButtonBlue"
                disabled={busy}
                onClick={reschedule}
              >
                Ruaj datat (→ Pending)
              </MondayButton>
            </div>

            <Popconfirm
              title="Fshi kërkesën?"
              onConfirm={remove}
              okText="Po"
              cancelText="Jo"
            >
              <MondayButton className="mondayButtonRed" disabled={busy}>
                Fshi
              </MondayButton>
            </Popconfirm>
          </>
        ) : null}
      </Space>
    </Drawer>
  );
};

export default PlanerDrawer;
